import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaUtensils,
  FaShoppingCart,
  FaMobileAlt,
  FaHome,
  FaCar,
} from "react-icons/fa";
import EatEzyLogo from "../components/Common/EatEzyLogo";
import LandingFooter from "../components/Common/LandingFooter";

export default function SaaSLandingPage() {
  const products = [
    {
      key: "eatezy",
      name: "EatEzy",
      segment: "Food Business",
      description:
        "Restaurant, cafe, cloud kitchen, food court — digital menu, WhatsApp ordering, offers, banners, reels, and full management.",
      to: "/eatezy",
      enabled: true,
      icon: <FaUtensils className="w-6 h-6 text-white" />,
      iconGradient: "linear-gradient(135deg, #14b8a6 0%, #f97316 100%)",
      accent: "teal",
    },
    {
      key: "retailezy",
      name: "RetailEzy",
      segment: "Supermarket / Retail",
      description:
        "For supermarkets & retail stores — product/catalog management, promotions, and customer-friendly ordering experience.",
      enabled: false,
      icon: <FaShoppingCart className="w-6 h-6 text-white" />,
      iconGradient: "linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)",
      accent: "sky",
    },
    {
      key: "techezy",
      name: "TechEzy",
      segment: "Mobile & Electronics",
      description:
        "For mobile, gadgets & electronics — inventory-style listings, offers, and quick customer enquiries & ordering.",
      enabled: false,
      icon: <FaMobileAlt className="w-6 h-6 text-white" />,
      iconGradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
      accent: "indigo",
    },
    {
      key: "realezy",
      name: "RealEzy",
      segment: "Real Estate",
      description:
        "For real-estate listings — properties, features, gallery/reels, and lead generation in one clean dashboard.",
      enabled: false,
      icon: <FaHome className="w-6 h-6 text-white" />,
      iconGradient: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
      accent: "rose",
    },
    {
      key: "fastezy",
      name: "FastEzy",
      segment: "Cars / Used Cars",
      description:
        "For cars & used cars — listings, highlights, reels/social links, and fast lead capture with a modern showcase.",
      enabled: false,
      icon: <FaCar className="w-6 h-6 text-white" />,
      iconGradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
      accent: "amber",
    },
  ];

  return (
    <main className="min-h-screen bg-white flex flex-col">
      <section className="relative flex-1 py-16 sm:py-20 bg-gradient-to-br from-gray-50 via-white to-teal-50/30 overflow-hidden">
        {/* Subtle background blobs */}
        <div className="absolute -top-24 -left-24 w-80 h-80 bg-teal-200/30 blur-3xl rounded-full" />
        <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-orange-200/30 blur-3xl rounded-full" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-10">
            <EatEzyLogo size="large" showTagline={false} />
          </div>

          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Choose your platform
            </h1>
            <p className="text-lg sm:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Transform your restaurant with our cloud-based SaaS platform. Create stunning digital menus, enable WhatsApp ordering, and manage multiple locations from one powerful dashboard.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-600">
              <span className="inline-flex items-center gap-2">
                <FaCheck className="text-emerald-600" /> Digital Menu
              </span>
              <span className="inline-flex items-center gap-2">
                <FaCheck className="text-emerald-600" /> WhatsApp Ordering
              </span>
              <span className="inline-flex items-center gap-2">
                <FaCheck className="text-emerald-600" /> Dashboard Management
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
            {products.map((p) => {
              const Card = (
                <div
                  className={`group rounded-2xl border bg-white p-6 shadow-sm transition-all duration-300 ${
                    p.enabled
                      ? "border-gray-200 hover:shadow-xl hover:-translate-y-0.5"
                      : "border-gray-200/80 opacity-90"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0"
                          style={{
                            backgroundImage: p.iconGradient,
                          }}
                          title={`${p.name} logo`}
                        >
                          {p.icon}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-gray-500 mb-0.5">
                            {p.segment}
                          </div>
                          <h2 className="text-2xl font-extrabold text-gray-900 leading-tight truncate">
                            {p.name}
                          </h2>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        {p.description}
                      </p>
                      {!p.enabled && (
                        <div className="mt-4 inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-600">
                          Coming soon
                        </div>
                      )}
                    </div>
                    <FaArrowRight
                      className={`w-5 h-5 mt-1 transition-transform ${
                        p.enabled
                          ? "text-teal-600 group-hover:translate-x-1"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
              );

              if (p.enabled && p.to) {
                return (
                  <Link key={p.key} to={p.to} className="block">
                    {Card}
                  </Link>
                );
              }

              return (
                <div key={p.key} className="block cursor-not-allowed">
                  {Card}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <LandingFooter />
    </main>
  );
}

